/**
 * ITSM Notification Section
 *
 * Displays connected IT Service Management integrations (Jira, ServiceNow)
 * in a TanStackTable with Edit (Jira only) + Delete actions.
 * Uses persistent header-in-card pattern (Option C) where header is always visible.
 *
 * Structure:
 * - Header Row: Always visible with icon, title, description, and "Add Integration" button
 * - Content Row: TanStackTable shown only when integrations exist (conditional)
 *
 * Used in:
 * - NotificationSettingsCards
 */
import { PlusCircleIcon, TicketIcon } from '@heroicons/react/24/solid';
import React, { useMemo } from 'react';

import { TanStackTable } from '@/components/table/TanStackTable';
import { Button } from '@/components/ui/button';
import { SettingContentCard } from '@/sections/settings/components/primitives/SettingContentCard';
import { Account } from '@/types';

import { getItsmColumns } from './columns/itsmColumns';

export interface ITSMSectionProps {
  /** Filtered ITSM integrations (Jira, ServiceNow) */
  notifications: Account[];
  /** Handler for viewing/editing ITSM integration (Jira only shows Edit) */
  onView: (integration: Account) => void;
  /** Handler for deleting ITSM integration (hidden when undefined) */
  onDelete?: (integration: Account) => void;
  /** Handler for adding new ITSM integration (hidden when undefined) */
  onAdd?: () => void;
  /** Loading status */
  status?: 'error' | 'success' | 'pending';
  /** Error object if status is error */
  error?: Error | null;
}

/**
 * ITSM Notification Section
 *
 * Displays connected IT Service Management integrations (Jira, ServiceNow)
 * with conditional Edit support (Jira only) + Delete actions.
 *
 * Migration from card-based layout to TanStackTable pattern,
 * matching Chat Messaging implementation.
 */
export const ITSMSection: React.FC<ITSMSectionProps> = ({
  notifications,
  onView,
  onDelete,
  onAdd,
  status = 'success',
  error = null,
}) => {
  // Column definitions with action handlers
  const columns = useMemo(
    () =>
      getItsmColumns({
        onView,
        onDelete,
      }),
    [onView, onDelete]
  );

  return (
    <section
      aria-labelledby="itsm-heading"
      data-testid="itsm-section"
      data-tutorial="notif-ticketing"
    >
      <SettingContentCard padding="sm">
        <div className="flex flex-col gap-4">
          {/* Header Row - ALWAYS visible */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TicketIcon className="size-5 shrink-0 opacity-50" />
                <div className="flex flex-col gap-1">
                  <h4 id="itsm-heading" className="font-bold">
                    IT Service Management
                  </h4>
                  <span className="text-chariot-text-secondary text-xs">
                    Integrate with Jira Cloud or ServiceNow for ticketing
                    workflows
                  </span>
                </div>
              </div>
              {onAdd && (
                <Button onClick={onAdd} size="sm" data-testid="itsm-add-button">
                  <PlusCircleIcon className="size-4" />
                  Add Integration
                </Button>
              )}
            </div>
          </div>

          {/* Table Row - Only when data exists */}
          {notifications.length > 0 && (
            <div className="border-chariot-stroke overflow-hidden rounded-lg border">
              <TanStackTable
                name="itsm"
                columns={columns}
                data={notifications}
                status={status}
                error={error}
              />
            </div>
          )}
        </div>
      </SettingContentCard>
    </section>
  );
};
